import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Loader2, ArrowRight } from "lucide-react";
import { BRAND, WHATSAPP_LINK, CALL_SALES, CALL_SERVICE, SERVICES } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: "CCTV Installation", address: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Thanks! We'll call you shortly.");
      setForm({
        name: "", phone: "", email: "", service: "CCTV Installation", address: "", message: "",
      });
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <Seo
        title="Contact Hayat Security — CCTV Installation & Repair Near Me in Nagpur"
        description="Need CCTV installation, AMC or repair near you in Nagpur? Call Hayat Security at +91 96738 78652 (Sales) or +91 84462 55253 (Service). UG-8, Raj Commercial Complex, Bhaldarpura Square, Nagpur 440018."
        canonicalPath="/contact"
      />
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">Contact</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-slate-900 tracking-tight mt-3">
              Let's secure your premises
            </h1>
            <p className="text-slate-600 mt-5 text-lg">
              Reach out via call, WhatsApp or the enquiry form — we typically respond the same day.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 space-y-5"
          >
            <a
              href={CALL_SALES}
              data-testid="contact-call-sales"
              className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 hover:border-[#1C6B8A] hover:bg-[#E8F4F8]/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-[#1C6B8A] text-white flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-slate-500 uppercase font-semibold">Sales</div>
                <div className="font-display font-semibold text-slate-900">{BRAND.salesDisplay}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1C6B8A] group-hover:translate-x-1 transition-all" />
            </a>

            <a
              href={CALL_SERVICE}
              data-testid="contact-call-service"
              className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 hover:border-[#1C6B8A] hover:bg-[#E8F4F8]/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-[#155670] text-white flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-slate-500 uppercase font-semibold">Service</div>
                <div className="font-display font-semibold text-slate-900">{BRAND.serviceDisplay}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1C6B8A] group-hover:translate-x-1 transition-all" />
            </a>

            <a
              href={WHATSAPP_LINK()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-[#25D366] text-white flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-slate-500 uppercase font-semibold">WhatsApp</div>
                <div className="font-display font-semibold text-slate-900">Chat with us</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
            </a>

            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email"
              className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 hover:border-[#1C6B8A] hover:bg-[#E8F4F8]/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500 uppercase font-semibold">Email</div>
                <div className="font-display font-semibold text-slate-900 truncate">{BRAND.email}</div>
              </div>
            </a>

            <div className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200">
              <div className="w-11 h-11 rounded-xl bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase font-semibold">Visit us</div>
                <div className="font-display font-semibold text-slate-900 mt-0.5 leading-snug">
                  {BRAND.address}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            <form
              onSubmit={submit}
              className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8 card-soft"
              data-testid="contact-form"
            >
              <h2 className="font-display text-2xl font-bold text-slate-900">Enquiry Form</h2>
              <p className="text-sm text-slate-500 mt-1">Fill the form and our team will get back within a few hours.</p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Name *</Label>
                  <Input id="c-name" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="contact-name" placeholder="Your name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone">Phone *</Label>
                  <Input id="c-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="contact-phone" placeholder="10-digit mobile" type="tel" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="c-email">Email (optional)</Label>
                  <Input id="c-email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-email-input" placeholder="you@example.com" type="email" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Service</Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger data-testid="contact-service-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                      ))}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="c-address">Address / Area</Label>
                  <Input id="c-address" value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="contact-address" placeholder="Area, locality, Nagpur" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="c-message">Message</Label>
                  <Textarea id="c-message" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="contact-message" placeholder="Tell us briefly about your requirement..." />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="mt-6 w-full h-12 rounded-full brand-btn font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Sending..." : "Request Free Quote"}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden border border-slate-200" data-testid="contact-map">
            <iframe
              title="Hayat Security Location"
              src="https://www.google.com/maps?q=Hayat+Enterprises+Bhaldarpura+Nagpur+Walker+Road&output=embed"
              width="100%"
              height="420"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
